import {ChangeDetectorRef, Component, forwardRef, Injector, Input, ViewChild} from '@angular/core';
import {
  ControlContainer,
  ControlValueAccessor,
  FormBuilder,
  FormControl,
  FormControlDirective,
  NG_VALUE_ACCESSOR,
  ReactiveFormsModule
} from "@angular/forms";
import {TranslatePipe, TranslateService} from "@ngx-translate/core";
import {distinctUntilChanged} from "rxjs";
import {isArray} from "ngx-bootstrap/chronos";
import {DateTimeSelectorComponent, TIME_MODE} from "../../form-inputs/date-time-selector/date-time-selector.component";
import {
  MultiSelectWithStaticOptionsStaticComponent
} from "../../form-inputs/multi-select-static-with-static-options/multi-select-with-static-options-static.component";
import {SelectOption} from "../../form-inputs/form-group/form-group.component";
import {Backend} from '@generated-backend-types';
import TableRelativeDateFilterEnum = Backend.App.Enums.TableRelativeDateFilterEnum;
import {NgIf} from '@angular/common';

@Component({
  selector: 'app-date-filter',
  templateUrl: './date-filter.component.html',
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => DateFilterComponent),
      multi: true,
    }
  ],
  imports: [MultiSelectWithStaticOptionsStaticComponent, DateTimeSelectorComponent, ReactiveFormsModule, TranslatePipe, NgIf],
  standalone: true
})
export class DateFilterComponent implements ControlValueAccessor {
  public dateSelector: SelectOption[] = [
    {
      value: this.translate.instant('enums.tableRelativeDateFilterEnum.from_until_this_key_is_not_part_of_the_enum'),
      key: 'from_until'
    },
    {
      value: this.translate.instant('enums.tableRelativeDateFilterEnum.' + TableRelativeDateFilterEnum.TODAY),
      key: TableRelativeDateFilterEnum.TODAY
    },
    {
      value: this.translate.instant('enums.tableRelativeDateFilterEnum.' + TableRelativeDateFilterEnum.TOMORROW),
      key: TableRelativeDateFilterEnum.TOMORROW
    },
    {
      value: this.translate.instant('enums.tableRelativeDateFilterEnum.' + TableRelativeDateFilterEnum.YESTERDAY),
      key: TableRelativeDateFilterEnum.YESTERDAY
    },
    {
      value: this.translate.instant('enums.tableRelativeDateFilterEnum.' + TableRelativeDateFilterEnum.LAST_WEEK),
      key: TableRelativeDateFilterEnum.LAST_WEEK
    },
    {
      value: this.translate.instant('enums.tableRelativeDateFilterEnum.' + TableRelativeDateFilterEnum.THIS_WEEK),
      key: TableRelativeDateFilterEnum.THIS_WEEK
    },
    {
      value: this.translate.instant('enums.tableRelativeDateFilterEnum.' + TableRelativeDateFilterEnum.NEXT_WEEK),
      key: TableRelativeDateFilterEnum.NEXT_WEEK
    },
    {
      value: this.translate.instant('enums.tableRelativeDateFilterEnum.' + TableRelativeDateFilterEnum.THIS_MONTH),
      key: TableRelativeDateFilterEnum.THIS_MONTH
    },
    {
      value: this.translate.instant('enums.tableRelativeDateFilterEnum.' + TableRelativeDateFilterEnum.NEXT_MONTH),
      key: TableRelativeDateFilterEnum.NEXT_MONTH
    },
    {
      value: this.translate.instant('enums.tableRelativeDateFilterEnum.' + TableRelativeDateFilterEnum.THIS_YEAR),
      key: TableRelativeDateFilterEnum.THIS_YEAR
    },
  ]

  public formGroup = this.fb.group({
    min: this.fb.control<string | null>(null),
    max: this.fb.control<string | null>(null),
    relative_date: this.fb.control<TableRelativeDateFilterEnum | null | 'from_until'>('from_until')
  });

  @Input() public placeholder = ''
  @Input() public label!: string


  @ViewChild(FormControlDirective, {static: true})
  public formControlDirective?: FormControlDirective;

  @Input()
  public formControl?: FormControl<string[] | string | null>

  @Input()
  public formControlName!: string;

  @Input()
  public name?: string;

  @Input()
  public inline = true;

  @Input()
  public multiLine = true;
  protected readonly TIME_MODE = TIME_MODE;

  @Input()
  public showLabel = true;

  constructor(
    private readonly cdr: ChangeDetectorRef,
    private readonly injector: Injector,
    private readonly translate: TranslateService,
    private readonly fb: FormBuilder) {
    this.formGroup.valueChanges.pipe(distinctUntilChanged((a, b) => JSON.stringify(a) === JSON.stringify(b))).subscribe(
      v => {

        if (this.formGroup.controls.relative_date.value == 'from_until' && this.formGroup.valid) {
          this.control.setValue((v.min ?? '') + ',' + (v.max ?? ''), {emitEvent: true});
        } else if (this.formGroup.controls.relative_date.value !== null && this.formGroup.controls.relative_date.value !== 'from_until') {
          this.control.setValue(this.formGroup.controls.relative_date.value, {emitEvent: true});
        } else if (this.formGroup.controls.relative_date.value == null) {
          this.control.setValue(null, {emitEvent: true});
        }

      }
    )
  }

  public get control(): FormControl<string[] | string | null> {
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    return <FormControl<string[] | string | null>>this.formControl ?? this.controlContainer.control.get(this.formControlName);
  }

  get controlContainer(): ControlContainer {
    return this.injector.get(ControlContainer);
  }

  registerOnTouched(fn: () => void): void {
    this.formControlDirective?.valueAccessor?.registerOnTouched(fn);
  }

  registerOnChange(fn: () => void): void {
    this.formControlDirective?.valueAccessor?.registerOnChange(fn);
  }

  writeValue(value: string[] | string | null): void {
    // First and second case is for relative_date/dateSelector => from_until
    // Third case is for a selected relative_date/dateSelector that is not from_until
    // Fourth case is when relative_date/dateSelector is null
    if (value && isArray(value) && value.length === 2) {
      this.formGroup.patchValue({
        min: value[0],
        max: value[1],
        relative_date: 'from_until'
      }, {emitEvent: false})
    } else if (value && typeof value === 'string' && value.includes(',')) {
      const split = value.split(',')
      this.formGroup.patchValue({
        min: split[0] ?? null,
        max: split[1] ?? null,
        relative_date: 'from_until'
      }, {emitEvent: false})
    } else if (value && typeof value === 'string') {
      this.formGroup.patchValue({
        min: null,
        max: null,
        relative_date: value as TableRelativeDateFilterEnum
      }, {emitEvent: false})
    } else {
      this.formGroup.reset({}, {emitEvent: false})
    }
    if (this.control.getRawValue() !== value) {
      this.control.reset(value)
    }
  }

  setDisabledState(): void {
    // da formControl und control identisch sind brauchen wir hier nichts weiter zu geben

  }
}
